import { nullSafeGet } from '../utils/DataAccessUtils';
import { STOPLIGHT_COLORS } from '../utils/DataFormatterUtils';

export const RFP_STATUS = {
	DRAFT: 'draft',
	ACTIVE: 'active',
	CLOSED: 'closed',
	AWARD: 'award',
};

export const BIDDER_STATUSES = {
	NO_RESPONSE: 'noResponse',
	BIDDING: 'bidding',
	NOT_BIDDING: 'notBidding',
	INTERESTED: 'interested',
};

export const AWARD_STATUSES = {
	Awarded: 'awarded',
	Pending: 'pending',
	Declined: 'declined',
	Expired: 'expired',
	AssignedSupplier: 'assignedSupplier',
};

export const BIDDER_STATUS_DISPLAY_NAMES = {
	[BIDDER_STATUSES.NO_RESPONSE]: 'No Response',
	[BIDDER_STATUSES.BIDDING]: 'Bidding',
	[BIDDER_STATUSES.NOT_BIDDING]: 'Not Bidding',
	[BIDDER_STATUSES.INTERESTED]: 'Interested and Preparing Proposal',
};

export const BIDDER_STATUS_DISPLAY_TEXT_COLORS = {
	[BIDDER_STATUSES.NO_RESPONSE]: 'rgb(11, 28, 111)',
	[BIDDER_STATUSES.BIDDING]: STOPLIGHT_COLORS.pureGreen,
	[BIDDER_STATUSES.NOT_BIDDING]: STOPLIGHT_COLORS.pureRed,
	[BIDDER_STATUSES.INTERESTED]: '#4a1e87',
};

export const BIDDER_STATUS_DISPLAY_BACKGROUND_COLORS = {
	[BIDDER_STATUSES.NO_RESPONSE]: 'rgb(225, 229, 249)',
	[BIDDER_STATUSES.BIDDING]: '#e6f9e7',
	[BIDDER_STATUSES.NOT_BIDDING]: '#f9e6e6',
	[BIDDER_STATUSES.INTERESTED]: '#e8def8',
};

export const AWARD_STATUS_DISPLAY_NAMES = {
	[AWARD_STATUSES.Pending]: 'Pending',
	[AWARD_STATUSES.Expired]: 'Expired',
	[AWARD_STATUSES.Declined]: 'Declined',
	[AWARD_STATUSES.Awarded]: 'Awarded',
	[AWARD_STATUSES.AssignedSupplier]: 'Pending',
};

export const AWARD_STATUS_DISPLAY_TEXT_COLORS = {
	[AWARD_STATUSES.Pending]: 'rgb(11, 28, 111)',
	[AWARD_STATUSES.Expired]: STOPLIGHT_COLORS.pureRed,
	[AWARD_STATUSES.Declined]: STOPLIGHT_COLORS.pureRed,
	[AWARD_STATUSES.Awarded]: STOPLIGHT_COLORS.pureGreen,
	[AWARD_STATUSES.AssignedSupplier]: '#4a1e87',
};

export const AWARD_STATUS_DISPLAY_BACKGROUND_COLORS = {
	[AWARD_STATUSES.Pending]: 'rgb(225, 229, 249)',
	[AWARD_STATUSES.Expired]: '#f9e6e6',
	[AWARD_STATUSES.Declined]: '#f9e6e6',
	[AWARD_STATUSES.Awarded]: '#e6f9e7',
	[AWARD_STATUSES.AssignedSupplier]: '#e8def8',
};

export const BidderCumulativeStatus = {
	Requested: 'Requested',
	Interested: 'Interested',
	Submitted: 'Submitted',
	UnderReview: 'Under Review',
	Awarded: 'Awarded',
	Declined: 'Declined',
	NotParticipate: 'Not Participating',
};

export const BIDDER_CUMMULATIVE_DISPLAY_BACKGROUND_COLORS = {
	[BidderCumulativeStatus.Requested]: 'rgb(225, 229, 249)',
	[BidderCumulativeStatus.Interested]: 'rgb(225, 229, 249)',
	[BidderCumulativeStatus.Submitted]: '#e6f9e7',
	[BidderCumulativeStatus.UnderReview]: '#e8def8',
	[BidderCumulativeStatus.Awarded]: '#e6f9e7',
	[BidderCumulativeStatus.Declined]: '#f9e6e6',
	[BidderCumulativeStatus.NotParticipate]: '#f9e6e6',
};

export const BIDDER_CUMMULATIVE_DISPLAY_TEXT_COLORS = {
	[BidderCumulativeStatus.Requested]: 'rgb(11, 28, 111)',
	[BidderCumulativeStatus.Interested]: 'rgb(11, 28, 111)',
	[BidderCumulativeStatus.Submitted]: STOPLIGHT_COLORS.pureGreen,
	[BidderCumulativeStatus.UnderReview]: '#4a1e87',
	[BidderCumulativeStatus.Awarded]: STOPLIGHT_COLORS.pureGreen,
	[BidderCumulativeStatus.Declined]: STOPLIGHT_COLORS.pureRed,
	[BidderCumulativeStatus.NotParticipate]: STOPLIGHT_COLORS.pureRed,
};

export const getBidderCummulativeStatusFromStatuses = (rfpStatus, bidderStatus, awardStatus) => {
	const isRequested =
		rfpStatus === RFP_STATUS.ACTIVE &&
		bidderStatus === BIDDER_STATUSES.NO_RESPONSE &&
		awardStatus === AWARD_STATUSES.Pending;

	const isInterested =
		rfpStatus === RFP_STATUS.ACTIVE &&
		bidderStatus === BIDDER_STATUSES.INTERESTED &&
		awardStatus === AWARD_STATUSES.Pending;

	const isSubmitted =
		rfpStatus === RFP_STATUS.ACTIVE &&
		bidderStatus === BIDDER_STATUSES.BIDDING &&
		(awardStatus === AWARD_STATUSES.Pending || awardStatus === AWARD_STATUSES.AssignedSupplier);

	const isUnderReview =
		rfpStatus === RFP_STATUS.CLOSED &&
		bidderStatus === BIDDER_STATUSES.BIDDING &&
		(awardStatus === AWARD_STATUSES.Pending || awardStatus === AWARD_STATUSES.AssignedSupplier);

	const isAwarded =
		rfpStatus === RFP_STATUS.AWARD &&
		bidderStatus === BIDDER_STATUSES.BIDDING &&
		awardStatus === AWARD_STATUSES.Awarded;

	const isDeclined =
		rfpStatus === RFP_STATUS.AWARD &&
		bidderStatus === BIDDER_STATUSES.BIDDING &&
		awardStatus === AWARD_STATUSES.Declined;

	if (isRequested) return BidderCumulativeStatus.Requested;
	if (isInterested) return BidderCumulativeStatus.Interested;
	if (isSubmitted) return BidderCumulativeStatus.Submitted;
	if (isUnderReview) return BidderCumulativeStatus.UnderReview;
	if (isAwarded) return BidderCumulativeStatus.Awarded;
	if (isDeclined) return BidderCumulativeStatus.Declined;

	return BidderCumulativeStatus.NotParticipate;
};

export const getBidderCummulativeStatus = (bidder) => {
	const bidderStatus = nullSafeGet('bidderStatus', bidder);
	const awardStatus = nullSafeGet('awardStatus', bidder);
	const rfpStatus = nullSafeGet('requestForProposal.status', bidder);

	return getBidderCummulativeStatusFromStatuses(rfpStatus, bidderStatus, awardStatus);
};
